import {
  depthLevel,
  follicleSpacingLevel,
  gapLevel,
  hairDensityLevel,
  levelClass,
  levelScore,
  scalpExposureLevel,
  thicknessLevel,
} from './levels';
import { CATEGORY_DEFINITIONS, levelMeaning } from './categoryCopy';
import { formatNumber, formatPercent } from './formatters';

export function buildCategoryInsights(metrics) {
  const insights = [];

  const hd = metrics.hair_density || {};
  const densityLevel = hairDensityLevel(hd.value_px || 0);
  insights.push({
    id: 'hair_density',
    title: CATEGORY_DEFINITIONS.hair_density.title,
    what: CATEGORY_DEFINITIONS.hair_density.what,
    level: densityLevel,
    levelClass: levelClass(densityLevel),
    score: levelScore('hair_density', densityLevel),
    meaning: levelMeaning(densityLevel),
    detail: `Coverage ratio: ${formatNumber(hd.value_px, 4)}`,
  });

  const exposure = metrics.scalp_exposure_ratio?.value || 0;
  const exposureLevel = scalpExposureLevel(exposure);
  insights.push({
    id: 'scalp_exposure',
    title: CATEGORY_DEFINITIONS.scalp_exposure.title,
    what: CATEGORY_DEFINITIONS.scalp_exposure.what,
    level: exposureLevel,
    levelClass: levelClass(exposureLevel),
    score: levelScore('scalp_exposure', exposureLevel),
    meaning: levelMeaning(exposureLevel),
    detail: `Visible scalp: ${formatPercent(exposure)} of image`,
  });

  const gap = metrics.hair_gap || {};
  const gapLvl = gapLevel(gap.average_px || 0);
  insights.push({
    id: 'hair_gap',
    title: CATEGORY_DEFINITIONS.hair_gap.title,
    what: CATEGORY_DEFINITIONS.hair_gap.what,
    level: gapLvl,
    levelClass: levelClass(gapLvl),
    score: levelScore('hair_gap', gapLvl),
    meaning: levelMeaning(gapLvl),
    detail: `Avg gap: ${formatNumber(gap.average_px)} px · ${gap.gap_count || 0} gaps detected`,
  });

  const thick = metrics.hair_thickness || {};
  const thickLevel = thick.level || thicknessLevel(thick.mean_px || 0);
  insights.push({
    id: 'hair_thickness',
    title: CATEGORY_DEFINITIONS.hair_thickness.title,
    what: CATEGORY_DEFINITIONS.hair_thickness.what,
    level: thickLevel,
    levelClass: levelClass(thickLevel),
    score: levelScore('hair_thickness', thickLevel),
    meaning: thick.level_description || levelMeaning(thickLevel),
    detail: `Mean: ${formatNumber(thick.mean_px)} px · Median: ${formatNumber(thick.median_px)} px`,
  });

  const fol = metrics.follicle_spacing || {};
  const folLevel = follicleSpacingLevel(fol.mean_px || 0);
  insights.push({
    id: 'follicle_spacing',
    title: CATEGORY_DEFINITIONS.follicle_spacing.title,
    what: CATEGORY_DEFINITIONS.follicle_spacing.what,
    level: folLevel,
    levelClass: levelClass(folLevel),
    score: levelScore('follicle_spacing', folLevel),
    meaning: levelMeaning(folLevel),
    detail: `${fol.follicle_point_count || 0} follicle points · avg spacing ${formatNumber(fol.mean_px)} px`,
  });

  const depth = metrics.depth || {};
  if (depth.available) {
    const hairDepthLevel = depthLevel(depth.relative_mean_hair || 0);
    insights.push({
      id: 'depth_hair',
      title: CATEGORY_DEFINITIONS.depth_hair.title,
      what: CATEGORY_DEFINITIONS.depth_hair.what,
      level: hairDepthLevel,
      levelClass: levelClass(hairDepthLevel),
      score: levelScore('depth', hairDepthLevel),
      meaning: levelMeaning(hairDepthLevel),
      detail: `Relative depth: ${formatNumber(depth.relative_mean_hair, 3)}`,
    });

    const scalpDepthLevel = depthLevel(depth.relative_mean_scalp || 0);
    insights.push({
      id: 'depth_scalp',
      title: CATEGORY_DEFINITIONS.depth_scalp.title,
      what: CATEGORY_DEFINITIONS.depth_scalp.what,
      level: scalpDepthLevel,
      levelClass: levelClass(scalpDepthLevel),
      score: levelScore('depth', scalpDepthLevel),
      meaning: levelMeaning(scalpDepthLevel),
      detail: `Relative depth: ${formatNumber(depth.relative_mean_scalp, 3)}`,
    });
  }

  return insights;
}
